"use client";

import useSWR, {
  mutate as globalMutate,
  unload,
  unstable_serialize,
  type Cache,
  type SWRConfiguration,
} from "swr";
import {
  type ActivityEntry,
  type Artifact,
  type BrandKit,
  type Connector,
  type DeckTheme,
  type FirmThemeStatus,
  type Deliverable,
  type MemoryDoc,
  type MemoryPreview,
  type ProjectSummary,
  type RoutineSummary,
  type CommandSummary,
  type Grader,
  type Skill,
  type SkillDetail,
  type SkillVersions,
  type Task,
  type TaskSummary,
  type MountedSourcesList,
  type UploadsList,
  type UsageSummary,
  type UserSkill,
  type WorkspaceSettings,
  getUsage,
  getWorkspaceSettings,
  listDeliverables,
  listGraders,
  listUserSkills,
  listActivity,
  listArtifacts,
  listConnectors,
  listMemory,
  listProjectMemories,
  listProjects,
  listRoutines,
  listCommands,
  listDeckThemes,
  firmThemeStatus,
  getBrandKit,
  listSkills,
  getSkillDetail,
  getSkillVersions,
  listTasks,
  listMountedProjectSources,
  listUploads,
  readProjectMemoryOptional,
  readMemoryOptional,
} from "./api";

// SWR keys are tuples — first element is a string namespace, rest are
// args. Hook helpers + invalidators live alongside their fetcher so the
// pairing stays in one place.

export const taskKeys = {
  list: () => ["tasks"] as const,
  detail: (id: string | null | undefined) =>
    id ? (["task", id] as const) : null,
};

export const projectKeys = {
  list: (archived: boolean = false) =>
    archived ? (["projects", "archived"] as const) : (["projects"] as const),
  memories: (projectId: string | null | undefined) =>
    projectId ? (["project-memories", projectId] as const) : null,
  memory: (projectId: string | null | undefined, topic: string | null) =>
    projectId && topic
      ? (["project-memory", projectId, topic] as const)
      : null,
};

export const memoryKeys = {
  list: () => ["memory"] as const,
  topic: (topic: string | null | undefined) =>
    topic ? (["workspace-memory", topic] as const) : null,
};

export const uploadKeys = {
  // Pass null/undefined for the global uploads list (used by /sources). A
  // string scopes to a project's sources rail.
  list: (projectId?: string | null) =>
    projectId ? (["uploads", projectId] as const) : (["uploads"] as const),
  mounted: (projectId?: string | null) =>
    projectId ? (["uploads-mounted", projectId] as const) : null,
};

export const artifactKeys = {
  list: () => ["artifacts"] as const,
};

export const deliverableKeys = {
  list: (params?: {
    project_id?: string;
    type?: string;
    task_id?: string;
    scope?: "mine" | "all";
  }) => (params ? (["deliverables", params] as const) : (["deliverables"] as const)),
};

export const activityKeys = {
  list: (limit = 50) => ["activity", limit] as const,
};

export const skillKeys = {
  list: (includeDisabled = false) => ["skills", includeDisabled] as const,
  detail: (name: string | null | undefined) =>
    name ? (["skill", name] as const) : null,
};

export const skillVersionKeys = {
  detail: (skillId: string | null | undefined) =>
    skillId ? (["skill-versions", skillId] as const) : null,
};

export const deckThemeKeys = {
  list: () => ["deck-themes"] as const,
  firm: () => ["deck-themes", "firm"] as const,
};

export const commandKeys = {
  list: (projectId?: string | null) =>
    ["commands", projectId ?? null] as const,
};

export const userSkillKeys = {
  list: () => ["user-skills"] as const,
};

export const graderKeys = {
  list: () => ["graders"] as const,
};

export const workspaceSettingsKeys = {
  detail: () => ["workspace-settings"] as const,
};

export const connectorKeys = {
  list: () => ["connectors"] as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

// Workspace usage + plan summary (drives the trial badge / upsell anywhere in the
// app, not just the Billing/Usage tabs). Cached app-wide via one SWR key.
export const usageKeys = {
  summary: () => ["usage", "summary"] as const,
};

export function useUsageSummary(opts?: SWRConfiguration<UsageSummary | null>) {
  return useSWR<UsageSummary | null>(
    usageKeys.summary(),
    () => getUsage("month"),
    { revalidateOnFocus: false, ...opts },
  );
}

export function useTasks(opts?: SWRConfiguration<TaskSummary[]>) {
  return useSWR<TaskSummary[]>(taskKeys.list(), () => listTasks(), {
    // Adaptive polling: faster while any task is running so the
    // running/done transition surfaces promptly.
    refreshInterval: (latest) =>
      Array.isArray(latest) && latest.some((t) => t.is_running)
        ? 2_000
        : 30_000,
    fallbackData: [],
    ...opts,
  });
}

// NB: there is deliberately no `useTask` hook. TasksPanel owns task detail in
// local state and bypasses the SWR read layer on purpose — see the "Why this
// panel bypasses the SWR read layer" block in tasks.tsx and primeTask/peekTask
// below. A naive `useTask` that TasksPanel then adopted would reintroduce the
// exact races that ownership model avoids (ENG-2926 step 5). The task DETAIL
// cache is still populated (primeTask) and read (peekTask) so other surfaces
// stay warm; it just isn't fronted by a subscribing hook.

export function useProjects(
  opts?: SWRConfiguration<ProjectSummary[]> & { archived?: boolean },
) {
  const archived = opts?.archived === true;
  return useSWR<ProjectSummary[]>(
    projectKeys.list(archived),
    () => listProjects({ archived }),
    {
      fallbackData: [],
      ...opts,
    },
  );
}

export function useProjectMemory(
  projectId: string | null | undefined,
  topic: string | null,
  opts?: SWRConfiguration<MemoryDoc | null>,
) {
  return useSWR<MemoryDoc | null>(
    projectKeys.memory(projectId, topic),
    async () =>
      projectId && topic
        ? await readProjectMemoryOptional(projectId, topic)
        : null,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      ...opts,
    },
  );
}

/** Cached read of a project's INSTRUCTIONS memory entry. 404 is the normal
 *  "no instructions yet" case and is cached as null — avoids repeat fetches
 *  on every project-home remount (ENG-2700). */
export function useProjectInstructions(
  projectId: string | null | undefined,
  opts?: SWRConfiguration<MemoryDoc | null>,
) {
  return useProjectMemory(projectId, projectId ? "INSTRUCTIONS" : null, {
    dedupingInterval: 60_000,
    ...opts,
  });
}

export function useWorkspaceMemory(
  topic: string | null | undefined,
  opts?: SWRConfiguration<MemoryDoc | null>,
) {
  return useSWR<MemoryDoc | null>(
    memoryKeys.topic(topic),
    async () => (topic ? await readMemoryOptional(topic) : null),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      ...opts,
    },
  );
}

export function useMemoryList(opts?: SWRConfiguration<MemoryPreview[]>) {
  return useSWR<MemoryPreview[]>(memoryKeys.list(), () => listMemory(), {
    fallbackData: [],
    ...opts,
  });
}

export type ProjectMemoryGroup = {
  project_id: string;
  entries: MemoryPreview[];
};

/** Fetch memory previews for many projects in parallel. Used by the
 * cross-project Memory inspector in Customize — the SWR key includes
 * a stable join of the project ids so the cache invalidates only when
 * the project set itself changes. */
export function useAllProjectMemories(
  projectIds: string[],
  opts?: SWRConfiguration<ProjectMemoryGroup[]>,
) {
  // Stable key: sorted+joined so the same set of ids in any order
  // shares one cache entry.
  const key = projectIds.length
    ? (["project-memories", "all", [...projectIds].sort().join(",")] as const)
    : null;
  return useSWR<ProjectMemoryGroup[]>(
    key,
    async () =>
      Promise.all(
        projectIds.map(async (id) => ({
          project_id: id,
          entries: await listProjectMemories(id),
        })),
      ),
    { fallbackData: [], ...opts },
  );
}

export function useUploads(
  projectId?: string | null,
  opts?: SWRConfiguration<UploadsList>,
) {
  return useSWR<UploadsList>(
    uploadKeys.list(projectId),
    async () =>
      projectId
        ? await listUploads({ projectId })
        : await listUploads(),
    { fallbackData: { uploads: [], all_tags: [] }, ...opts },
  );
}

export function useMountedProjectSources(
  projectId?: string | null,
  opts?: SWRConfiguration<MountedSourcesList>,
) {
  return useSWR<MountedSourcesList>(
    uploadKeys.mounted(projectId),
    projectId ? () => listMountedProjectSources(projectId) : null,
    { fallbackData: { sources: [] }, ...opts },
  );
}

export function useArtifacts(opts?: SWRConfiguration<Artifact[]>) {
  return useSWR<Artifact[]>(artifactKeys.list(), () => listArtifacts(), {
    refreshInterval: 10_000,
    fallbackData: [],
    ...opts,
  });
}

export function useDeliverables(
  params?: { project_id?: string; type?: string; task_id?: string; scope?: "mine" | "all" },
  opts?: SWRConfiguration<Deliverable[]>,
) {
  return useSWR<Deliverable[]>(
    deliverableKeys.list(params),
    () => listDeliverables(params),
    { refreshInterval: 30_000, fallbackData: [], ...opts },
  );
}

export function useActivity(
  limit = 50,
  opts?: SWRConfiguration<ActivityEntry[]>,
) {
  return useSWR<ActivityEntry[]>(
    activityKeys.list(limit),
    () => listActivity(limit),
    { refreshInterval: 10_000, fallbackData: [], ...opts },
  );
}

/** One resolved skill with its SKILL.md body — backs the skill detail page.
 *  Pass null to skip the fetch (e.g. when no skill is selected). */
export function useSkillDetail(
  name: string | null | undefined,
  opts?: SWRConfiguration<SkillDetail | null>,
) {
  return useSWR<SkillDetail | null>(
    skillKeys.detail(name),
    () => (name ? getSkillDetail(name) : null),
    opts,
  );
}

export function useSkills(includeDisabled = false, opts?: SWRConfiguration<Skill[]>) {
  return useSWR<Skill[]>(
    skillKeys.list(includeDisabled),
    () => listSkills({ includeDisabled }),
    { fallbackData: [], ...opts },
  );
}

/** The selectable deck house styles (default + OOB themes). Static catalog —
 *  revalidate rarely. */
export function useDeckThemes(opts?: SWRConfiguration<DeckTheme[]>) {
  return useSWR<DeckTheme[]>(deckThemeKeys.list(), () => listDeckThemes(), {
    revalidateOnFocus: false,
    fallbackData: [],
    ...opts,
  });
}

/** Whether the active workspace has a deck-relevant firm brand. Tenant-scoped, so
 *  it re-fetches on workspace switch (the key is stable but SWR is per-auth). */
export function useFirmThemeStatus(opts?: SWRConfiguration<FirmThemeStatus>) {
  return useSWR<FirmThemeStatus>(deckThemeKeys.firm(), () => firmThemeStatus(), {
    revalidateOnFocus: false,
    ...opts,
  });
}

export const brandKitKeys = { detail: () => ["brand-kit"] as const };

/** The workspace's resolved brand-kit (the house style decks render from).
 *  Admin-only surface; the editor loads it once and saves via updateBrandKit,
 *  then revalidates this key. */
export function useBrandKit(opts?: SWRConfiguration<BrandKit>) {
  return useSWR<BrandKit>(brandKitKeys.detail(), () => getBrandKit(), {
    revalidateOnFocus: false,
    ...opts,
  });
}

/** Versions + published pointer for one deck-skill. Pass null to skip the
 *  fetch (e.g. a row that isn't expanded yet). */
export function useSkillVersions(
  skillId: string | null,
  opts?: SWRConfiguration<SkillVersions | null>,
) {
  return useSWR<SkillVersions | null>(
    skillVersionKeys.detail(skillId),
    () => getSkillVersions(skillId as string),
    opts,
  );
}

export function invalidateSkillVersions(skillId: string) {
  return globalMutate(skillVersionKeys.detail(skillId));
}

export function useCommands(
  projectId?: string | null,
  opts?: SWRConfiguration<CommandSummary[]>,
) {
  return useSWR<CommandSummary[]>(
    commandKeys.list(projectId),
    () => listCommands({ projectId }),
    { fallbackData: [], ...opts },
  );
}

export function useUserSkills(opts?: SWRConfiguration<UserSkill[]>) {
  return useSWR<UserSkill[]>(userSkillKeys.list(), () => listUserSkills(), {
    fallbackData: [],
    ...opts,
  });
}

export function useGraders(opts?: SWRConfiguration<Grader[]>) {
  return useSWR<Grader[]>(graderKeys.list(), () => listGraders(), {
    fallbackData: [],
    ...opts,
  });
}

export function invalidateGraders() {
  return globalMutate(graderKeys.list());
}

// Effective workspace settings for the active tenant. Read by both composers
// to resolve the workspace-level Verification default (the fallback below a
// project's `verification_default`). Cached so the composer doesn't refetch on
// every keystroke; the Workspace settings panel calls `invalidateWorkspaceSettings`
// after a PATCH so the new default takes effect without a reload.
export function useWorkspaceSettings(
  opts?: SWRConfiguration<WorkspaceSettings>,
) {
  return useSWR<WorkspaceSettings>(
    workspaceSettingsKeys.detail(),
    () => getWorkspaceSettings(),
    opts,
  );
}

export function invalidateWorkspaceSettings() {
  return globalMutate(workspaceSettingsKeys.detail());
}

// Write the server's returned settings straight into the SWR cache (no
// revalidate). Used after an admin saves on the Workspace settings page so a
// composer reading useWorkspaceSettings sees the new default immediately — a
// plain revalidate is a no-op when no composer hook is currently mounted, which
// would otherwise let a returning composer render the stale cached value.
export function mutateWorkspaceSettings(next: WorkspaceSettings) {
  return globalMutate(workspaceSettingsKeys.detail(), next, { revalidate: false });
}

// ---------------------------------------------------------------------------
// Identity/tenancy boundaries — impersonation start/end, and any SPA-only
// switch of the active workspace that can't fall back on a full page reload
// (a reload's own teardown is what protects the ordinary workspace-switch
// path; see setActiveWorkspace).
//
// A per-key invalidate — or even a blanket `mutate(() => true, ...)` — only
// forgets what's already resolved. Neither one reliably stops a response
// already in flight for the PREVIOUS identity from landing after the switch
// and repopulating the cache under the new one: a matcher-based mutate only
// matches keys that already hold a cache entry, so a request that hasn't
// written anything back yet (e.g. a plain in-flight `mutate(key, promise)`)
// is invisible to it and survives untouched. `unload()` tracks in-flight
// requests and mutations directly instead of going through the cache, so it
// discards them regardless, closing that window.
// ---------------------------------------------------------------------------

export function unloadIdentityScopedCache() {
  unload({ revalidate: true });
}

// ---------------------------------------------------------------------------
// Invalidation helpers — replace the legacy CustomEvent bus.
// ---------------------------------------------------------------------------

/** Refetch the global tasks list. Sidebar Recents and per-project task
 * lists subscribe to this same key, so one call refreshes both. */
export function invalidateDeliverables() {
  return globalMutate((key) => Array.isArray(key) && key[0] === "deliverables");
}

export function invalidateTasks() {
  return globalMutate(taskKeys.list());
}

/** Optimistically flag a task as running (and not waiting) in the sidebar
 * list cache, without hitting the network. Used at stream start so the
 * running dot surfaces instantly and the adaptive poll flips to fast mode —
 * instead of a revalidation that can race the server's own state change and
 * cache the pre-run snapshot (e.g. an answered question still showing its
 * orange `waiting` dot). Reconciled by the next real revalidation; a no-op if
 * the task isn't in the cached list yet (brand-new tasks revalidate on the
 * stream's first event). */
export function markTaskRunningLocally(taskId: string) {
  const startedAt = new Date().toISOString();
  return globalMutate<TaskSummary[]>(
    taskKeys.list(),
    (cur) =>
      Array.isArray(cur)
        ? cur.map((t) =>
            t.id === taskId
              ? {
                  ...t,
                  is_running: true,
                  waiting: false,
                  // Mirror server list sort: freeze position at run start
                  // instead of stale updated_at while the list revalidates.
                  running_since: t.running_since ?? startedAt,
                }
              : t,
          )
        : cur,
    { revalidate: false },
  );
}

/** Write a freshly fetched task into the SWR detail cache without
 * revalidating. TasksPanel owns task detail in local state (there is no
 * subscribing `useTask` hook — see the note above), so this is what keeps a
 * stale-while-revalidate snapshot warm: re-opening a task can paint it
 * instantly via peekTask instead of flashing the restore skeleton. */
export function primeTask(task: Task) {
  const key = taskKeys.detail(task.id);
  if (key) void globalMutate(key, task, { revalidate: false });
}

/** Read-only peek at a task's cached detail, or undefined on a cold cache.
 * Needs the SWR cache instance (from useSWRConfig) since it reads outside a
 * hook; serialization is centralized here so callers don't reach into SWR
 * key internals. */
export function peekTask(
  cache: Cache,
  id: string | null | undefined,
): Task | undefined {
  const key = taskKeys.detail(id);
  if (!key) return undefined;
  const entry = cache.get(unstable_serialize(key)) as
    | { data?: Task }
    | undefined;
  return entry?.data;
}

/** Refetch project Sources (uploads scoped to this project). Pass null
 * to refresh the global uploads list as well. */
export function invalidateUploads(projectId?: string | null) {
  if (projectId) return globalMutate(uploadKeys.list(projectId));
  return Promise.all([
    globalMutate(uploadKeys.list()),
    globalMutate((key) => Array.isArray(key) && key[0] === "uploads"),
  ]);
}

export function invalidateProjects() {
  // Refresh both the active list and the archived list so a flip from
  // one to the other shows up immediately in either view.
  return Promise.all([
    globalMutate(projectKeys.list(false)),
    globalMutate(projectKeys.list(true)),
  ]);
}

export function invalidateProjectMemories(projectId: string) {
  // A memory write also advances the store's `updated_at`, which the projects
  // grid renders ("Updated …") and sorts by. Focus revalidation is disabled
  // (see swr-provider), so refresh the project list too or the footer + recent
  // sort show stale data after an in-app edit in the same session.
  return Promise.all([
    globalMutate(projectKeys.memories(projectId)),
    globalMutate(projectKeys.memory(projectId, "INSTRUCTIONS")),
    invalidateProjects(),
  ]);
}

export function invalidateMemoryList() {
  return globalMutate(memoryKeys.list());
}

export function invalidateUserSkills() {
  return globalMutate(userSkillKeys.list());
}

export function invalidateSkills() {
  // Refresh both the default and the admin (include-disabled) variants.
  return globalMutate((key) => Array.isArray(key) && key[0] === "skills");
}

/** Revalidate the detail cache and return the canonical response to callers
 * that must verify a write rather than merely wait for cache invalidation. */
export function invalidateSkillDetail(name: string): Promise<SkillDetail | null | undefined> {
  return globalMutate<SkillDetail | null>(skillKeys.detail(name));
}

export function useConnectors(opts?: SWRConfiguration<Connector[]>) {
  return useSWR<Connector[]>(connectorKeys.list(), () => listConnectors(), {
    fallbackData: [],
    ...opts,
  });
}

export function invalidateConnectors() {
  return globalMutate(connectorKeys.list());
}

const routineKeys = {
  list: () => "routines_list",
};

export function useRoutines(
  opts?: SWRConfiguration<RoutineSummary[]> & { enabled?: boolean },
) {
  // `enabled` is ours (null key when false). Poll every 30s so a fire that
  // lands while the tab is open shows up on the detail panel and the in-app
  // toast watcher (ENG-5319) without a dedicated endpoint.
  const { enabled = true, ...swrOpts } = opts ?? {};
  return useSWR<RoutineSummary[]>(
    enabled ? routineKeys.list() : null,
    () => listRoutines(),
    { refreshInterval: 30_000, ...swrOpts },
  );
}

export function invalidateRoutines() {
  return globalMutate(routineKeys.list());
}
